import { DashboardWidgetId } from "@/lib/types/dashboard-widgets";
import { EnrollmentStatus, Origin } from "@/openapi/generated";
import type { GetEnrollmentResponse } from "@/openapi/generated/models";
import type { WidgetDataItemFromApi } from "./widgets-client";

/** Widget keys fetched via POST /api/widgets/data for enrollment dashboard cards. */
export const STUDIO_ENROLLMENT_WIDGET_KEYS: readonly DashboardWidgetId[] = [
  DashboardWidgetId.TopClasses,
  DashboardWidgetId.EnrollmentsCalendar,
  DashboardWidgetId.EnrollmentStatus,
] as const;

export type StudioTopClassItem = {
  classId: number;
  className: string;
  enrollmentCount: number;
};

export type StudioEnrollmentStatusSummary = {
  pending: number;
  enrolled: number;
  awaiting: number;
  total: number;
};

export type ParsedEnrollmentWidgets = {
  topClasses: StudioTopClassItem[];
  enrollmentStatus: StudioEnrollmentStatusSummary | null;
  enrollments: GetEnrollmentResponse[];
};

const EMPTY_PARSED: ParsedEnrollmentWidgets = {
  topClasses: [],
  enrollmentStatus: null,
  enrollments: [],
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object";
}

function parseFiniteNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseDate(value: unknown): Date | null {
  if (value == null || value === "") return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === "number" && Number.isFinite(value)) {
    const ms = value < 1e12 ? value * 1000 : value;
    const parsed = new Date(ms);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function splitStudentName(full: string): {
  firstName: string;
  lastName: string;
} {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "Unknown", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

function mapEnrollmentStatus(status: string): EnrollmentStatus | null {
  const normalized = status.trim().toLowerCase();
  if (!normalized) return null;
  const match = Object.values(EnrollmentStatus).find(
    (value) => value.toLowerCase() === normalized,
  );
  return match ?? null;
}

function findWidgetData(
  dataItems: WidgetDataItemFromApi[],
  widgetId: DashboardWidgetId,
): Record<string, unknown> | null {
  const row = dataItems.find((item) => item.key === widgetId);
  if (!row?.data || !isRecord(row.data)) return null;
  return row.data;
}

export function parseTopClassesFromWidgetData(
  dataItems: WidgetDataItemFromApi[],
): StudioTopClassItem[] {
  const data = findWidgetData(dataItems, DashboardWidgetId.TopClasses);
  if (!data) return [];
  if (data.$type !== "top-classes") return [];
  if (!Array.isArray(data.items)) return [];

  return data.items
    .filter(isRecord)
    .map((row) => {
      const classId = parseFiniteNumber(row.classId);
      const enrollmentCount = parseFiniteNumber(row.enrollmentsCount);
      if (classId == null || enrollmentCount == null) return null;
      const className =
        typeof row.className === "string" && row.className.trim()
          ? row.className.trim()
          : `Class ${classId}`;
      return { classId, className, enrollmentCount };
    })
    .filter((row): row is StudioTopClassItem => row != null)
    .sort((a, b) => b.enrollmentCount - a.enrollmentCount);
}

export function parseEnrollmentStatusFromWidgetData(
  dataItems: WidgetDataItemFromApi[],
): StudioEnrollmentStatusSummary | null {
  const data = findWidgetData(dataItems, DashboardWidgetId.EnrollmentStatus);
  if (!data) return null;
  if (data.$type !== "enrollment-status") return null;
  if (!Array.isArray(data.items)) return null;

  let pending = 0;
  let enrolled = 0;
  let awaiting = 0;

  for (const row of data.items) {
    if (!isRecord(row)) continue;
    const count = parseFiniteNumber(row.count) ?? 0;
    const status =
      typeof row.status === "string" ? row.status.toLowerCase() : "";
    if (status === EnrollmentStatus.Pending.toLowerCase()) pending += count;
    else if (status === EnrollmentStatus.Enrolled.toLowerCase())
      enrolled += count;
    else if (status === EnrollmentStatus.Awaiting.toLowerCase())
      awaiting += count;
  }

  const total = pending + enrolled + awaiting;
  if (total === 0) return null;

  return { pending, enrolled, awaiting, total };
}

export function enrollmentCalendarRowToGetEnrollmentResponse(
  row: Record<string, unknown>,
): GetEnrollmentResponse | null {
  const enrollmentId = parseFiniteNumber(row.enrollmentId);
  const classId = parseFiniteNumber(row.classId);
  if (enrollmentId == null || classId == null) return null;

  const studentName =
    typeof row.studentName === "string" && row.studentName.trim()
      ? row.studentName.trim()
      : "Unknown Student";
  const className =
    typeof row.className === "string" && row.className.trim()
      ? row.className.trim()
      : "Unknown Class";
  const enrolledAt = parseDate(row.date);
  const mappedStatus =
    typeof row.status === "string" ? mapEnrollmentStatus(row.status) : null;
  // Unknown/missing calendar status is not a review-queue item.
  const status = mappedStatus ?? EnrollmentStatus.Enrolled;
  const { firstName, lastName } = splitStudentName(studentName);
  const studentId = parseFiniteNumber(row.studentId);

  return {
    enrollmentId,
    enrolledAt,
    status,
    updatedAt: enrolledAt ?? new Date(),
    statusChangedAt: null,
    droppedAt: null,
    dropDate: null,
    rejectedAt: null,
    cancelledAt: null,
    completedAt: null,
    origin: Origin.Member,
    processingTaskId: null,
    processingTaskStatus: null,
    activityHistory: [],
    student: {
      studentId: studentId ?? 0,
      firstName,
      lastName,
      email: null,
      isActive: true,
    },
    _class: {
      classId,
      name: className,
      description: null,
      isActive: true,
    },
  };
}

export function parseEnrollmentsCalendarFromWidgetData(
  dataItems: WidgetDataItemFromApi[],
): GetEnrollmentResponse[] {
  const data = findWidgetData(dataItems, DashboardWidgetId.EnrollmentsCalendar);
  if (!data) return [];
  if (data.$type !== "enrollment-calendar") return [];
  if (!Array.isArray(data.items)) return [];

  return data.items
    .filter(isRecord)
    .map(enrollmentCalendarRowToGetEnrollmentResponse)
    .filter((row): row is GetEnrollmentResponse => row != null);
}

export function parseEnrollmentWidgetsFromWidgetData(
  dataItems: WidgetDataItemFromApi[],
): ParsedEnrollmentWidgets {
  if (!dataItems.length) return EMPTY_PARSED;

  return {
    topClasses: parseTopClassesFromWidgetData(dataItems),
    enrollmentStatus: parseEnrollmentStatusFromWidgetData(dataItems),
    enrollments: parseEnrollmentsCalendarFromWidgetData(dataItems),
  };
}

export function applyEnrollmentWidgetData<
  T extends {
    topClasses: StudioTopClassItem[];
    enrollmentStatus: StudioEnrollmentStatusSummary;
    enrollments: GetEnrollmentResponse[];
  },
>(base: T, parsed: ParsedEnrollmentWidgets): T {
  return {
    ...base,
    topClasses:
      parsed.topClasses.length > 0 ? parsed.topClasses : base.topClasses,
    enrollmentStatus:
      parsed.enrollmentStatus != null
        ? parsed.enrollmentStatus
        : base.enrollmentStatus,
    // Prefer studio enrollment records (they keep enrolledAt time-of-day).
    // Calendar widget payloads often send a date-only `date` field.
    enrollments:
      base.enrollments.length > 0 ? base.enrollments : parsed.enrollments,
  };
}
